import {
  type CellKeyDownEvent,
  type ValueSetterParams,
} from "ag-grid-community";
import { type CustomCellRendererProps } from "ag-grid-react";
import { CheckCircleIcon, CloseIcon, ShareIcon, Users02Icon } from "vega-icons";

import { getFullName } from "@vega-api/custom/utils/user";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { type Currency } from "@vega-api/generated/types/currency";
import { type DealShortlistItemResponse } from "@vega-api/generated/types/dealShortlistItemResponse";
import { ActionMenu } from "@vega-core/components/ActionMenu/ActionMenu";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ClientAvatar } from "@vega-core/components/ClientAvatar/ClientAvatar";
import { DropdownCellEditor } from "@vega-core/components/DataGrid/DropdownCellEditor";
import { MoneyAbbreviationCellEditor } from "@vega-core/components/DataGrid/MoneyAbbreviationCellEditor";
import { useColumnToggle } from "@vega-core/components/DataGrid/useColumnToggle";
import { suppressActionsTabKey } from "@vega-core/components/DataGrid/utils";
import { EMPTY_CELL_VALUE } from "@vega-core/constants/global";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { formatMoney, formatMoneyShort } from "@vega-core/utils/formatMoney";

import { DraftStatusButton } from "../components/DraftEmail/DraftStatusButton";
import { type ProspectWithMemberCount } from "../components/OutreachTable/hooks/useOutreachManagement";
import { PersonCell } from "../components/PersonCell/PersonCell";
import { TIER_OPTIONS } from "../constants/prospectConstants";
import { ShareTabType } from "../constants/sharePageConstants";

import {
  deriveExportColumns,
  type ExportableColDef,
  toGridColumns,
} from "./deriveExportColumns";
import { useCoInvestDealReadOnly } from "./useCoInvestDealReadOnly";

export const SHARE_TABLE_COL_IDS = {
  limitedPartner: "limitedPartner",
  email: "email",
  access: "access",
  actions: "actions",
  tier: "tier",
  estimatedTicketSizeMin: "estimatedTicketSizeMin",
  estimatedTicketSizeMax: "estimatedTicketSizeMax",
  probabilityWeightedMidPoint: "probabilityWeightedMidPoint",
  icgPriority: "icgPriority",
  lpSma: "lpSma",
  organizationChannel: "organizationChannel",
  relationship: "relationship",
  addedBy: "addedBy",
  notes: "notes",
} as const;

const actionButtonClassName =
  "box-border h-[35px] min-h-[35px] shrink-0 px-2.5 py-0";

const formatAccessLabel = (memberCount: number | undefined): string => {
  if (!memberCount || memberCount <= 0) {
    return m.coInvestments_share_noAccessYet();
  }

  if (memberCount === 1) return m.coInvestments_share_accessUser();

  return m.coInvestments_share_accessUsers({ count: memberCount });
};

export function TierCellRenderer({
  data,
  value,
}: {
  value?: string | null;
  data?: DealShortlistItemResponse | null;
}) {
  const tier = data?.tier ?? value ?? "";

  return (
    <div
      className="flex items-center h-full"
      data-testid={data ? `cell-${data.id}-tier` : undefined}
    >
      <ClientAvatar
        isBold
        name={tier}
        size="small"
        variant="primaryBackground"
      />
    </div>
  );
}

type UseShareTableColumnsParams = {
  activeTab: ShareTabType;
  onShare?: (prospect: ProspectWithMemberCount) => void;
  onDraft?: (prospect: ProspectWithMemberCount) => void;
  dealId?: string;
  canAddShortlistItems?: boolean;
  canApprove?: boolean;
  currency?: Currency;
  onApproveProspect?: (prospectId: string) => void;
  approvingProspectId?: string | null;
  onRemoveProspect?: (prospectId: string) => void;
  removingProspectId?: string | null;
  onUpdateDataPoints?: (dealOpportunityId: string) => void;
  onCopyLink?: (dealLink: string) => void;
  onOpenOwnerModal?: (prospect: ProspectWithMemberCount) => void;
};

export const useShareTableColumns = ({
  activeTab,
  onShare,
  onDraft,
  canAddShortlistItems = false,
  canApprove,
  currency,
  onApproveProspect,
  approvingProspectId,
  onRemoveProspect,
  removingProspectId,
  onUpdateDataPoints,
  onCopyLink,
  onOpenOwnerModal,
}: UseShareTableColumnsParams) => {
  const { isMutationLocked, isCancelledDeal } = useCoInvestDealReadOnly();
  const { featureFlag } = useFeatureFlag();
  const isOutreachEmailEnabled =
    featureFlag[FeatureFlag.COINVEST_OUTREACH_EMAIL_VD_27885];
  // Closed deals lock all shortlist mutations except the notes column;
  // cancelled deals lock the notes column too
  const canMutateShortlist = !isMutationLocked;
  const showEmailAndAccessColumns =
    isOutreachEmailEnabled &&
    activeTab === ShareTabType.Approved &&
    canMutateShortlist;

  let actionsColumnWidth: number | undefined;

  if (isMutationLocked) {
    actionsColumnWidth = 100;
  } else if (showEmailAndAccessColumns) {
    actionsColumnWidth = 108;
  } else if (isOutreachEmailEnabled) {
    actionsColumnWidth = 320;
  }

  const limitedPartnerColumn: ExportableColDef<ProspectWithMemberCount> = {
    colId: SHARE_TABLE_COL_IDS.limitedPartner,
    headerName: m.coInvestments_prospects_limitedPartner(),
    field: "limitedPartner.name",
    width: 300,
    pinned: "left",
    exportValue: (row) => row.limitedPartner.name || EMPTY_CELL_VALUE,
    cellRenderer: ({
      data,
    }: CustomCellRendererProps<ProspectWithMemberCount>) => {
      if (!data) return null;

      return (
        <div
          className="h-full flex justify-between items-center gap-5"
          data-testid={`cell-${data.id}-limitedPartner`}
        >
          <span className="body-base-medium truncate">
            {data.limitedPartner.name || ""}
          </span>
        </div>
      );
    },
  };

  const renderEmailButton = (
    data: ProspectWithMemberCount,
    rowIndex: number,
  ) =>
    isOutreachEmailEnabled && onDraft && canMutateShortlist ? (
      <DraftStatusButton
        dataTestId={`draft-button-${rowIndex}`}
        disabled={data.isClosed}
        onClick={() => onDraft(data)}
        teaserEmail={data.teaserEmail}
      />
    ) : null;

  const renderAccessButton = (
    data: ProspectWithMemberCount,
    rowIndex: number,
  ) => {
    if (!canMutateShortlist) return null;

    const manageAccessLabel = m.coInvestments_share_manageAccessTitle({
      lpName: data.limitedPartner.name,
    });

    return (
      <ThemedButton
        ariaLabel={manageAccessLabel}
        className="p-0"
        dataTestId={`contacts-button-${rowIndex}`}
        onClick={() => onShare?.(data)}
        tooltip={manageAccessLabel}
        variant="text"
      >
        <span className="text-left whitespace-nowrap">
          {formatAccessLabel(data.memberCount)}
        </span>
      </ThemedButton>
    );
  };

  const actionsColumn: ExportableColDef<ProspectWithMemberCount> = {
    colId: SHARE_TABLE_COL_IDS.actions,
    headerName: m.common_actions(),
    sortable: false,
    toggleable: true,
    width: actionsColumnWidth,
    suppressKeyboardEvent: suppressActionsTabKey,
    cellRenderer: ({
      data,
      node,
    }: CustomCellRendererProps<ProspectWithMemberCount>) => {
      if (!data) return null;
      const rowIndex = node.rowIndex ?? 0;

      const removeButton =
        onRemoveProspect &&
        data.canRemoveFromShortlist &&
        canMutateShortlist ? (
          <ThemedButton
            ariaLabel={m.coInvestments_prospects_remove_button()}
            className={cnTwMerge(
              actionButtonClassName,
              !showEmailAndAccessColumns && "ml-auto",
            )}
            dataTestId={`remove-prospect-button-${rowIndex}`}
            disabled={removingProspectId === data.id}
            icon={<CloseIcon className="text-text/negative size-4" />}
            iconPosition="left"
            onClick={() => onRemoveProspect(data.id)}
            size="small"
            tooltip={m.coInvestments_prospects_remove_button()}
            variant="secondary"
          />
        ) : null;

      if (activeTab === ShareTabType.Approved) {
        const {
          canSendDeal,
          memberCount,
          isClosed: isDealOpportunityClosed,
        } = data;

        return (
          <div className="flex items-center gap-2 h-full">
            {showEmailAndAccessColumns
              ? null
              : renderEmailButton(data, rowIndex)}

            {!isOutreachEmailEnabled &&
            canSendDeal &&
            onShare &&
            canMutateShortlist ? (
              <ThemedButton
                className="min-h-7 h-9 px-3"
                dataTestId={`share-button-${rowIndex}`}
                disabled={isDealOpportunityClosed}
                icon={
                  isDealOpportunityClosed ? undefined : (
                    <ShareIcon className="size-4" />
                  )
                }
                iconPosition="right"
                onClick={() => onShare(data)}
                size="small"
                variant={isDealOpportunityClosed ? "secondary" : "primary"}
              >
                {isDealOpportunityClosed
                  ? m.coInvestments_share_opportunityClosed()
                  : m.btn_share()}
              </ThemedButton>
            ) : null}

            {canSendDeal ? (
              <ActionMenu
                className="flex items-center [&>button]:min-h-7 [&>button]:size-9"
                menuItems={[
                  ...(onUpdateDataPoints && canMutateShortlist
                    ? [
                        {
                          title: m.coInvestments_outreach_updateDataPoints(),
                          onClick: () =>
                            onUpdateDataPoints(data.dealOpportunityId),
                        },
                      ]
                    : []),
                  ...(onCopyLink
                    ? [
                        {
                          title: m.btn_copyLink(),
                          onClick: () => onCopyLink(data.dealLink),
                        },
                      ]
                    : []),
                ]}
              />
            ) : null}
            {!showEmailAndAccessColumns &&
            (isOutreachEmailEnabled ||
              (memberCount != null && memberCount > 0)) &&
            canMutateShortlist ? (
              <ThemedButton
                ariaLabel={
                  isOutreachEmailEnabled
                    ? m.coInvestments_share_manageAccessTitle({
                        lpName: data.limitedPartner.name,
                      })
                    : undefined
                }
                className="mr-1 p-0"
                dataTestId={`contacts-button-${rowIndex}`}
                icon={<Users02Icon className="size-4" />}
                iconPosition="left"
                onClick={() => onShare?.(data)}
                tooltip={
                  isOutreachEmailEnabled
                    ? m.coInvestments_share_manageAccessTitle({
                        lpName: data.limitedPartner.name,
                      })
                    : undefined
                }
                variant="text"
              >
                <span className="w-4 text-left">
                  {memberCount ? String(memberCount) : null}
                </span>
              </ThemedButton>
            ) : null}
            {removeButton}
          </div>
        );
      }

      const { id: prospectId, isApproved } = data;
      const isApproving = approvingProspectId === prospectId;

      return (
        <div className="flex items-center gap-2 h-full">
          {canApprove &&
          canMutateShortlist &&
          onApproveProspect &&
          !isApproved ? (
            <ThemedButton
              className={actionButtonClassName}
              dataTestId={`approve-prospect-button-${rowIndex}`}
              disabled={isApproving}
              icon={<CheckCircleIcon className="size-4 text-text/positive" />}
              iconPosition="left"
              onClick={() => onApproveProspect(prospectId)}
              size="small"
              variant="secondary"
            >
              {m.coInvestments_prospects_approve()}
            </ThemedButton>
          ) : null}
          {removeButton}
        </div>
      );
    },
  };

  const emailColumn: ExportableColDef<ProspectWithMemberCount> = {
    colId: SHARE_TABLE_COL_IDS.email,
    headerName: m.coInvestments_share_emailColumn(),
    sortable: false,
    toggleable: true,
    width: 190,
    suppressKeyboardEvent: suppressActionsTabKey,
    cellRenderer: ({
      data,
      node,
    }: CustomCellRendererProps<ProspectWithMemberCount>) => {
      if (!data) return null;

      return (
        <div className="flex items-center h-full">
          {renderEmailButton(data, node.rowIndex ?? 0)}
        </div>
      );
    },
  };

  const accessColumn: ExportableColDef<ProspectWithMemberCount> = {
    colId: SHARE_TABLE_COL_IDS.access,
    headerName: m.coInvestments_share_accessColumn(),
    sortable: false,
    toggleable: true,
    width: 124,
    suppressKeyboardEvent: suppressActionsTabKey,
    cellRenderer: ({
      data,
      node,
    }: CustomCellRendererProps<ProspectWithMemberCount>) => {
      if (!data) return null;

      return (
        <div className="flex items-center h-full">
          {renderAccessButton(data, node.rowIndex ?? 0)}
        </div>
      );
    },
  };

  const tierColumn: ExportableColDef<ProspectWithMemberCount> = {
    toggleable: true,
    colId: SHARE_TABLE_COL_IDS.tier,
    headerName: m.coInvestments_prospects_tier(),
    field: "tier",
    width: 80,
    exportValue: (row) => row.tier || EMPTY_CELL_VALUE,
    editable: canAddShortlistItems && canMutateShortlist,
    cellEditor: DropdownCellEditor,
    cellEditorParams: {
      options: TIER_OPTIONS,
      renderOption: TierCellRenderer,
    },
    cellRenderer: TierCellRenderer,
    cellEditorPopup: true,
    cellEditorPopupPosition: "under",
  };

  const estimatedTicketSizeMinColumn: ExportableColDef<ProspectWithMemberCount> =
    {
      toggleable: true,
      colId: SHARE_TABLE_COL_IDS.estimatedTicketSizeMin,
      headerName: m.coInvestments_prospects_estimatedTicketSizeMin(),
      width: 150,
      exportValue: (row) =>
        row.estimatedTicketSizeMin
          ? formatMoney(row.estimatedTicketSizeMin)
          : EMPTY_CELL_VALUE,
      editable: canAddShortlistItems && canMutateShortlist,
      cellEditor: MoneyAbbreviationCellEditor,
      cellEditorParams: (params: { data: DealShortlistItemResponse }) => ({
        currency,
        customValidationErrors: (value: number | null) => {
          const maxAmount = params.data.estimatedTicketSizeMax?.amount;

          if (value !== null && maxAmount !== undefined && value > maxAmount) {
            return [
              m.coInvestments_gpEstimatedTicketSize_modal_minNotExceedMax(),
            ];
          }

          return null;
        },
      }),
      valueGetter: (params) =>
        params.data?.estimatedTicketSizeMin?.amount ?? null,
      valueSetter: (
        params: ValueSetterParams<ProspectWithMemberCount, number | null>,
      ) => {
        const newAmount = params.newValue;

        if (newAmount === null || newAmount === undefined) return false;

        const existingCurrency =
          params.data.estimatedTicketSizeMin?.currency ??
          params.data.estimatedTicketSizeMax?.currency ??
          currency;

        if (!existingCurrency) return false;

        params.data.estimatedTicketSizeMin = {
          amount: newAmount,
          currency: existingCurrency,
        };

        return true;
      },
      cellRenderer: ({
        data,
      }: CustomCellRendererProps<ProspectWithMemberCount>) => {
        if (!data) return null;
        const formatted = data.estimatedTicketSizeMin
          ? formatMoneyShort(data.estimatedTicketSizeMin)
          : "—";

        return (
          <span data-testid={`cell-${data.id}-estimatedTicketSizeMin`}>
            {formatted}
          </span>
        );
      },
    };

  const estimatedTicketSizeMaxColumn: ExportableColDef<ProspectWithMemberCount> =
    {
      toggleable: true,
      colId: SHARE_TABLE_COL_IDS.estimatedTicketSizeMax,
      headerName: m.coInvestments_prospects_estimatedTicketSizeMax(),
      width: 150,
      exportValue: (row) =>
        row.estimatedTicketSizeMax
          ? formatMoney(row.estimatedTicketSizeMax)
          : EMPTY_CELL_VALUE,
      editable: canAddShortlistItems && canMutateShortlist,
      cellEditor: MoneyAbbreviationCellEditor,
      cellEditorParams: (params: { data: DealShortlistItemResponse }) => ({
        currency,
        customValidationErrors: (value: number | null) => {
          const minAmount = params.data.estimatedTicketSizeMin?.amount;

          if (value !== null && minAmount !== undefined && value < minAmount) {
            return [
              m.coInvestments_gpEstimatedTicketSize_modal_maxAtLeastMin(),
            ];
          }

          return null;
        },
      }),
      valueGetter: (params) =>
        params.data?.estimatedTicketSizeMax?.amount ?? null,
      valueSetter: (
        params: ValueSetterParams<ProspectWithMemberCount, number | null>,
      ) => {
        const newAmount = params.newValue;

        if (newAmount === null || newAmount === undefined) return false;

        const existingCurrency =
          params.data.estimatedTicketSizeMax?.currency ??
          params.data.estimatedTicketSizeMin?.currency ??
          currency;

        if (!existingCurrency) return false;

        params.data.estimatedTicketSizeMax = {
          amount: newAmount,
          currency: existingCurrency,
        };

        return true;
      },
      cellRenderer: ({
        data,
      }: CustomCellRendererProps<ProspectWithMemberCount>) => {
        if (!data) return null;
        const formatted = data.estimatedTicketSizeMax
          ? formatMoneyShort(data.estimatedTicketSizeMax)
          : "—";

        return (
          <span data-testid={`cell-${data.id}-estimatedTicketSizeMax`}>
            {formatted}
          </span>
        );
      },
    };

  const probabilityWeightedMidPointColumn: ExportableColDef<ProspectWithMemberCount> =
    {
      toggleable: true,
      colId: SHARE_TABLE_COL_IDS.probabilityWeightedMidPoint,
      headerName: m.coInvestments_prospects_probabilityWeightedMidPoint(),
      width: 180,
      field: "probabilityWeightedMidPoint.amount",
      exportValue: (row) =>
        row.probabilityWeightedMidPoint
          ? formatMoney(row.probabilityWeightedMidPoint)
          : EMPTY_CELL_VALUE,
      cellRenderer: ({
        data,
      }: CustomCellRendererProps<ProspectWithMemberCount>) => {
        if (!data) return null;
        const formatted = data.probabilityWeightedMidPoint
          ? formatMoneyShort(data.probabilityWeightedMidPoint)
          : "—";

        return (
          <span data-testid={`cell-${data.id}-probabilityWeightedMidPoint`}>
            {formatted}
          </span>
        );
      },
    };

  const icgPriorityColumn: ExportableColDef<ProspectWithMemberCount> = {
    toggleable: true,
    colId: SHARE_TABLE_COL_IDS.icgPriority,
    headerName: m.coInvestments_prospects_icgPriority(),
    width: 100,
    field: "icgPriority",
    exportValue: (row) => {
      if (row.icgPriority == null) return EMPTY_CELL_VALUE;

      return row.icgPriority ? m.common_yes() : m.common_no();
    },
    cellRenderer: ({
      data,
    }: CustomCellRendererProps<ProspectWithMemberCount>) => {
      if (!data) return null;

      let label: string;

      if (data.icgPriority == null) {
        label = "—";
      } else if (data.icgPriority) {
        label = m.common_yes();
      } else {
        label = m.common_no();
      }

      return <span data-testid={`cell-${data.id}-icgPriority`}>{label}</span>;
    },
  };

  const lpSmaColumn: ExportableColDef<ProspectWithMemberCount> = {
    toggleable: true,
    colId: SHARE_TABLE_COL_IDS.lpSma,
    headerName: m.coInvestments_prospects_lpSma(),
    width: 100,
    field: "lpSma",
    exportValue: (row) => {
      if (row.lpSma == null) return EMPTY_CELL_VALUE;

      return row.lpSma ? m.common_yes() : m.common_no();
    },
    cellRenderer: ({
      data,
    }: CustomCellRendererProps<ProspectWithMemberCount>) => {
      if (!data) return null;

      let label: string;

      if (data.lpSma == null) {
        label = "—";
      } else if (data.lpSma) {
        label = m.common_yes();
      } else {
        label = m.common_no();
      }

      return <span data-testid={`cell-${data.id}-lpSma`}>{label}</span>;
    },
  };

  const organizationChannelColumn: ExportableColDef<ProspectWithMemberCount> = {
    toggleable: true,
    colId: SHARE_TABLE_COL_IDS.organizationChannel,
    headerName: m.coInvestments_prospects_organizationChannel(),
    field: "organizationChannel",
    exportValue: (row) => row.organizationChannel || EMPTY_CELL_VALUE,
    cellRenderer: ({
      data,
    }: CustomCellRendererProps<ProspectWithMemberCount>) => {
      if (!data) return null;

      return (
        <span data-testid={`cell-${data.id}-organizationChannel`}>
          {data.organizationChannel || "—"}
        </span>
      );
    },
  };

  const relationshipColumn: ExportableColDef<ProspectWithMemberCount> = {
    toggleable: true,
    colId: SHARE_TABLE_COL_IDS.relationship,
    headerName: m.coInvestments_prospects_opportunityOwner(),
    width: 370,
    valueGetter: ({ data }) =>
      data?.relationship?.fullName
        ? getFullName(data.relationship.fullName)
        : "",
    exportValue: (row) => {
      if (!row.relationship) return EMPTY_CELL_VALUE;

      return `${getFullName(row.relationship.fullName)} (${row.relationship.email})`;
    },
    cellRenderer: ({
      data,
    }: CustomCellRendererProps<ProspectWithMemberCount>) => {
      if (!data) return null;

      const content = (
        <PersonCell
          email={data.relationship?.email}
          name={
            data.relationship
              ? getFullName(data.relationship.fullName)
              : undefined
          }
        />
      );

      if (canAddShortlistItems && canMutateShortlist && onOpenOwnerModal) {
        return (
          <button
            className="inline-flex items-center h-full w-full focus-outline-themed"
            data-testid={`cell-${data.id}-relationship`}
            onClick={() => onOpenOwnerModal(data)}
            type="button"
          >
            {content}
          </button>
        );
      }

      return (
        <div
          className="flex items-center h-full"
          data-testid={`cell-${data.id}-relationship`}
        >
          {content}
        </div>
      );
    },
  };

  const addedByColumn: ExportableColDef<ProspectWithMemberCount> = {
    toggleable: true,
    colId: SHARE_TABLE_COL_IDS.addedBy,
    headerName: m.coInvestments_prospects_addedBy(),
    width: 370,
    valueGetter: ({ data }) =>
      data?.addedBy?.fullName ? getFullName(data.addedBy.fullName) : "",
    exportValue: (row) => {
      if (!row.addedBy) return EMPTY_CELL_VALUE;

      return `${getFullName(row.addedBy.fullName)} (${row.addedBy.email})`;
    },
    cellRenderer: ({
      data,
    }: CustomCellRendererProps<ProspectWithMemberCount>) => {
      if (!data) return null;

      return (
        <div
          className="flex items-center h-full"
          data-testid={`cell-${data.id}-addedBy`}
        >
          <PersonCell
            email={data.addedBy?.email}
            name={data.addedBy ? getFullName(data.addedBy.fullName) : undefined}
          />
        </div>
      );
    },
  };

  const notesColumn: ExportableColDef<ProspectWithMemberCount> = {
    toggleable: true,
    colId: SHARE_TABLE_COL_IDS.notes,
    headerName: m.coInvestments_outreach_notes(),
    field: "notes",
    sortable: false,
    exportValue: (row) => row.notes || EMPTY_CELL_VALUE,
    editable: canAddShortlistItems && !isCancelledDeal,
    cellEditor: "agLargeTextCellEditor",
    cellEditorParams: { rows: 3 },
    cellEditorPopup: true,
    cellRenderer: ({
      data,
    }: CustomCellRendererProps<ProspectWithMemberCount>) => {
      if (!data) return null;

      return (
        <span className="truncate" data-testid={`cell-${data.id}-notes`}>
          {data.notes || "—"}
        </span>
      );
    },
  };

  // Allow "relationship" column to open edit modal on Enter or F2 key press
  const onCellKeyDown = (e: CellKeyDownEvent<ProspectWithMemberCount>) => {
    if (e.column.getColId() !== SHARE_TABLE_COL_IDS.relationship) return;

    if (
      !e.data ||
      !canAddShortlistItems ||
      !canMutateShortlist ||
      !onOpenOwnerModal
    )
      return;

    const key = (e.event as KeyboardEvent).key;

    if (key === "Enter" || key === "F2") {
      onOpenOwnerModal(e.data);
    }
  };

  const rawColumns: ExportableColDef<ProspectWithMemberCount>[] = [
    limitedPartnerColumn,
    ...(showEmailAndAccessColumns ? [emailColumn, accessColumn] : []),
    // On locked deals the actions cell is always empty — drop the column entirely
    ...(isMutationLocked && activeTab === ShareTabType.Pending
      ? []
      : [actionsColumn]),
    tierColumn,
    estimatedTicketSizeMinColumn,
    estimatedTicketSizeMaxColumn,
    probabilityWeightedMidPointColumn,
    icgPriorityColumn,
    lpSmaColumn,
    organizationChannelColumn,
    activeTab === ShareTabType.Approved ? relationshipColumn : addedByColumn,
    notesColumn,
  ];

  const { columns: exportColumns } = deriveExportColumns(rawColumns);
  const { columnDefs: columns, renderColumnToggle } = useColumnToggle(
    toGridColumns(rawColumns),
  );

  return {
    columns,
    onCellKeyDown,
    renderColumnToggle,
    exportColumns,
  };
};
